// `configureAgentsData` — the injected-config seam, born with the harness UI
// intake and with exactly the three inputs that intake could not carry across.
//
// UNTIL NOW THIS PACKAGE DELIBERATELY HAD NO SEAM, and the reason it has one is
// a change of subject rather than a change of mind. `data/index.ts` argued the
// absence on measurement: nothing app-coupled crossed for the two data
// families, because transport resolves from the `@heidi/api` registry, the
// query client comes from `useQueryClient()`, the flag/auth gate crosses as an
// `enabled` parameter and user-visible effects are ports. All of that still
// holds for `/data`. What arrived is `src/ui/agent-harness`, whose modules mint
// the ml-scribe origin and the SSE request headers — three reads of DEPLOYMENT
// and PLATFORM state that no parameter can carry, because the callers that
// would have to supply them are the app files this move is retiring.
//
// The five REQUIRED inputs, and why each is a getter rather than a value:
//
//   * `resolveApiOrigin` — `getDomains().baseV2Url`. Region-resolved and
//     environment-resolved, so it must not be read at import time. This is the
//     same port `UserEventsPorts.resolveApiOrigin` already carries for the
//     user-wide event transport, deliberately spelled the same way.
//   * `resolveWebsocketOrigin` — `getDomains().websocketV1Url`, and a SECOND
//     port rather than a reuse of the one above because the two are measurably
//     different values: `baseV2Url` is `https://<host>/api/v2/ml-scribe/` and
//     `websocketV1Url` is a bare `wss://<host>` with no path, from a different
//     `@/utils/endpoints` resolver (`getWebsocketUrlFromConfig`). The voice
//     socket appends its own `/api/v2/ml-scribe/voice/realtime`, so feeding it
//     `resolveApiOrigin` would produce a doubled path against an `https://`
//     scheme — a broken URL, not a near miss.
//   * `getClientHeaders` — the `X-Client-Id` / `X-Client-Version` pair. Reads
//     `isTauri()` and the app version.
//   * `getAuthTokenSync` — the SYNCHRONOUS auth-token read. Its app
//     implementation walks the auth-cache suppression atom, the external-launch
//     token, the FHIR provider branch and a flag before it reaches the cached
//     access token; none of that is a package concern and all of it changes
//     while the app runs.
//   * `getWorkspaceId` — the active workspace id, the same one that stamps
//     `Heidi-Workspace-Id` on HTTP and on SSE. A browser cannot set a header on
//     a WebSocket upgrade, so the voice handshake carries it as a query param
//     instead. `null` is a real answer and travels as an omitted param, which
//     the backend reads as "infer the user's default workspace".
//
// WHAT IS NOT HERE, so the omission reads as a measurement:
//
//   * the query client, in EITHER shape — the `queryClient: QueryClient`
//     instance five other packages carry, or the `getQueryClient` getter this
//     seam declared for two carves and never cashed. Every
//     moved hook calls `useQueryClient()` in its own body, exactly as it did
//     app-side, so no carved module reads a field. The getter was held open for
//     ONE named future adopter, `src/features/tool-approvals/resolve-overlay-approval.ts`
//     — a module-scope resolver factory with zero production importers, which
//     that ruling deleted outright rather than moved. With its only adopter
//     gone the member was a crossing declared for nobody, and a declared
//     crossing that nothing reads is not a cheap option: it is a shape the next
//     module reaches for instead of taking its `QueryClient` as a parameter.
//     The three parameterised legs (`ProjectsBackgroundRunner.tsx`,
//     `voice-assistant/controller.ts`, `applyUiAction.tsx`) were never going to
//     adopt it — they hold the client only to hand it to a callee that already
//     takes one explicitly. A module here that genuinely needs a cache handle
//     outside a render re-declares it, required, in the PR that has the reader.
//   * a raw flag read. The Inbox gate needs the Heidi Work verdict, which ORs
//     PostHog `release-enable-heidi-ii` — a provider this package cannot read —
//     so the resolved verdict crosses as `useIsHeidiWorkEnabled` below.
//     `packageUi.agents` (`/ui`) still records `flags.disposition: none`; this
//     member lives in `/data`.
//   * `useAuthToken`, the reactive redux read. Authentication and the overlay
//     token-sync read come from `@heidi/auth/state`. What still crosses here
//     is whether the signed-in identity has hydrated. That is not the token,
//     and it cannot be recomputed from `getAuthTokenSync`.
//
// The seam is in `/data` and not beside its `/ui` consumers because that is
// where all seven precedents put it and where `src/composition/hosts/agents/agents-data-wiring.ts`
// expects to find it; `/ui` may read `/data`, and the reverse is what the
// boundary fixture forbids.

import type { createStore } from 'jotai';

import type { ClientCapabilities, WorkflowRun } from '../contracts';
import type { UserEventsPorts } from './events/user-events-client';
import type { RoutineActionedPort, RoutineRunPorts } from './mutation/routineMutations';
import type { WorkHomeModeSettingsPort } from './projects/useWorkHomeModeSetting';

type JotaiStore = ReturnType<typeof createStore>;

export interface AgentsDataConfig {
  /**
   * The deployment's ml-scribe v2 origin, WITH its trailing path
   * (`https://host/api/v2/ml-scribe/`) — `getDomains().baseV2Url` app-side.
   * Callers strip trailing slashes themselves, as the app module did.
   */
  resolveApiOrigin: () => string;
  /**
   * The deployment's WebSocket origin, WITHOUT a path
   * (`wss://host`) — `getDomains().websocketV1Url` app-side. Callers append
   * their own path, as the app module did.
   */
  resolveWebsocketOrigin: () => string;
  /** `X-Client-Id` plus `X-Client-Version` when the build has one. */
  getClientHeaders: () => Record<string, string>;
  /**
   * Synchronous auth-token read for SSE request headers.
   *
   * `undefined` and `null` are BOTH meaningful and are passed through: the app
   * helper returns `undefined` when the auth cache is suppressed, and the
   * header factory must omit `Authorization` rather than stamp
   * `Bearer undefined`.
   */
  getAuthTokenSync: () => string | null | undefined;
  /** The sync read never refreshes, so a stream that gets a 401 awaits this before retrying. */
  getAuthToken?: UserEventsPorts['getAuthToken'];
  /**
   * Synchronous read of the active workspace id — `getWorkspaceId()` app-side,
   * the same source that stamps `Heidi-Workspace-Id` on HTTP and on SSE.
   *
   * REQUIRED, though `null` is a valid answer: the handshake omits the param
   * for `null` and the backend infers the user's default workspace. Optional
   * here would let a bootstrap drop the port, typecheck, and silently send
   * every voice session to that default.
   */
  getWorkspaceId: () => string | null;
  /**
   * Has the signed-in identity finished hydrating — `!!useUser().data`
   * app-side, the INC-215 boot-window proof.
   *
   * The Inbox reads are gated on it so a stale boot-window token cannot fire
   * them. Authentication is a different question and does not answer this one:
   * the provider can call a session authenticated while the user query is
   * still in flight, which is the exact window the incident was.
   *
   * A HOOK — the answer flips from false to true during a boot, and a captured
   * boolean freezes the verdict at whatever it was when the app wired the seam.
   */
  useIsIdentityHydrated?: () => boolean;
  /**
   * The app's Heidi Work verdict — `useIsProjectsEnabled()` from
   * `src/features/flags/heidi-work-umbrella.ts` (PostHog `release-enable-heidi-ii`
   * OR the platform's Flagsmith Projects key). Gates the Inbox reads so they open
   * for exactly the users who see Work.
   *
   * A HOOK: the flag providers resolve after boot and can change while the app
   * runs.
   */
  useIsHeidiWorkEnabled?: () => boolean;
  /**
   * The app's layered-settings read/write pair (`getResolvedSettings` and
   * `patchSettings` from `@/pages/api/scribeAPIV2`) that the Work home mode
   * hooks call. Wired once, so every render reads the same object.
   */
  workHomeModeSettings?: WorkHomeModeSettingsPort;
  /**
   * Clears the library document preview. Optional store so the non-hook reset
   * can target the store it is already writing; the hook form calls it bare.
   */
  clearLibraryDocumentPreview?: (store?: JotaiStore) => void;
  /**
   * The desktop bridge's capability read, for pages that never load the Work
   * host. `errorKind` classifies a bridge rejection without its message.
   */
  desktopClientCapabilities?: {
    isAvailable(): boolean;
    getCapabilities(): Promise<
      Omit<ClientCapabilities, 'platform_system'> & { platform_system: string }
    >;
    errorKind(error: unknown): string;
  };
  /** Chat-sidebar list keys. Imported at the wiring, never re-declared. */
  chatListQueryKeys?: { chats: string; savedChats: string };
  /** App-owned routines gates, read on each hook call. */
  useIsWorkflowsEnabled?: () => boolean;
  useIsSessionRoutineRunsEnabled?: () => boolean;
  /** App analytics reactions for routine mutations. */
  routinesAnalytics?: {
    onRoutineActioned: NonNullable<RoutineActionedPort['onRoutineActioned']>;
    onRunClicked: NonNullable<RoutineRunPorts['onRunClicked']>;
  };
  /** Resolve optional desktop request data for an on-demand run. */
  resolveWorkflowRunRequestExtras?: NonNullable<
    RoutineRunPorts['resolveRunRequestExtras']
  >;
  /** Refresh the app's credit allowance after an exhausted run. */
  invalidateCreditAllowance?: () => Promise<void>;
  /** The app's Slide Builder verdict, read per call by the open-artifact mapper. */
  isSlideBuilderEnabled?: () => boolean;
  /** Evidence's in-session surface gate, read per call by the surface ui-tool. */
  isInSessionSurfaceEnabled?: () => boolean;
  /** Stamps a surfaced card onto the in-flight session routine, when there is one. */
  resolveSurfaceRunAttribution?: (
    runs: Array<
      Pick<
        WorkflowRun,
        'source_workflow_id' | 'agent_run_id' | 'status' | 'created_at' | 'updated_at'
      >
    >
  ) => { sourceWorkflowId: string | null; agentRunId: string | null };
}

let config: AgentsDataConfig | null = null;

export const configureAgentsData = (next: AgentsDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getAgentsDataConfig = (): AgentsDataConfig => {
  if (!config) {
    throw new Error(
      '[agents/data] not configured — app bootstrap must call configureAgentsData() ' +
        '(see src/composition/hosts/agents/agents-data-wiring.ts); tests exercising the harness ' +
        'transport must configure a test double first.'
    );
  }
  return config;
};

/** Test-only reset so a suite cannot leak a double into the next one. */
export const resetAgentsDataConfigForTests = (): void => {
  config = null;
};
